import { useState, useContext } from 'react';
import { useNavigate } from "react-router-dom";

import { Grid, TextField, Button, CircularProgress } from '@material-ui/core';
import { Snackbar } from '@material-ui/core';
import Logo from '../assets/img/new-bleat-logo.png';

import '../App.scss';

import AuthContext from '../store/AuthContext.js';

import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined.js';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined.js';

const LOGIN_VIEW = "login";
const RESET_REQUEST_VIEW = "request-reset";
const DEFAULT_LOGIN_ERROR = "Sorry, email or password incorrect";
const RESET_LINK_SENT_MESSAGE = "Password reset link has been sent to your registered email.";
const INVALID_EMAIL_MESSAGE = "Please enter a valid email address.";

const Login = () => {
	const navigate = useNavigate();
	const authCtx = useContext(AuthContext);

	const [authView, setAuthView] = useState(LOGIN_VIEW);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState(DEFAULT_LOGIN_ERROR);
	const [showPassword, setShowPassword] = useState(false);

	const emailRegex = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

	const showSnackbar = (message) => {
		setSnackbarMessage(message);
		setOpenSnackbar(true);
	};

	const checkForEnter = (event, action) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			action();
		}
	};

	const goToLoginView = () => {
		setAuthView(LOGIN_VIEW);
		setPassword('');
		navigate('/login', { replace: true });
	};

	const goToResetRequestView = () => {
		setAuthView(RESET_REQUEST_VIEW);
		setPassword('');
		navigate('/login', { replace: true });
	};

	const submit = async () => {
		if (!emailRegex.test(email)) {
			showSnackbar(INVALID_EMAIL_MESSAGE);
			return;
		}

		setIsSubmitting(true);

		try {
			const response = await fetch(process.env.REACT_APP_API_URI + '/v1/adminlogin', {
				method: 'POST',
				mode: 'cors',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					email,
					password,
				}),
			});
			const data = await response.json();

			if (response.ok && data.status === "success") {
				const tokenExpiration = new Date(new Date().getTime() + 86400000);
				authCtx.login(
					data.data.token,
					data.data.userId,
					data.data.userName,
					data.data.userEmail,
					data.data.userScope,
					data.data.accountId,
					tokenExpiration
				);
				navigate("/overview", { replace: true });
				return;
			}

			showSnackbar(data.message || DEFAULT_LOGIN_ERROR);
		} catch (error) {
			showSnackbar("Unable to sign in right now.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const passwordReset = async () => {
		if (!emailRegex.test(email)) {
			showSnackbar(INVALID_EMAIL_MESSAGE);
			return;
		}

		setIsSubmitting(true);

		try {
			const resetUrl = new URL('/reset-password', window.location.origin).toString();
			const response = await fetch(process.env.REACT_APP_API_URI + '/v2/administrators/reset', {
				method: 'POST',
				mode: 'cors',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email: email.trim(), url: resetUrl }),
			});
			const data = await response.json();

			if (response.ok && data.status === "success") {
				showSnackbar(RESET_LINK_SENT_MESSAGE);
				goToLoginView();
				return;
			}

			showSnackbar(data.message || "Unable to send reset link.");
		} catch (error) {
			showSnackbar("Unable to send reset link.");
		} finally {
			setIsSubmitting(false);
		}
	};

	if (authView === LOGIN_VIEW) {
		return (
			<div className="login-register">
				<div className="login">
					<img src={Logo} alt="Bleat Logo" className='bleat-logo' />

					<div className="form-heading">
						<h1>Hello</h1>
						<p>Sign in to your account</p>
					</div>

					<div className="form-wrapper">
						<form onKeyDown={(event) => checkForEnter(event, submit)}>
							<Grid container>
								<Grid item>
									<p className='helper-text'>Email</p>
									<TextField onChange={(event) => setEmail(event.target.value)} value={email} variant="outlined" fullWidth required />
								</Grid>

								<Grid item>
									<p className='helper-text'>Password</p>
									<div className="password-container">
										<TextField onChange={(event) => setPassword(event.target.value)} value={password} type={!showPassword ? "password" : "text"} variant="outlined" fullWidth required />
										<span className='eye-icon' onClick={() => setShowPassword((prev) => !prev)}>
											{showPassword ? <VisibilityOffOutlinedIcon/> : <RemoveRedEyeOutlinedIcon/>}
										</span>
									</div>
								</Grid>
							</Grid>

							{isSubmitting ? <Button className="outlineBtn"><CircularProgress size={20} /></Button> : <Button className="outlineBtn" onClick={submit}>Sign in</Button>}
							<p className="forgot-password">
								<button type="button" onClick={goToResetRequestView}>Forgot password</button>
							</p>
						</form>
					</div>

					<Snackbar open={openSnackbar} autoHideDuration={2000} onClose={() => setOpenSnackbar(false)} message={snackbarMessage}></Snackbar>
				</div>
			</div>
		);
	}

	return (
		<div className="login-register">
			<div className="login">
				<img src={Logo} alt="Bleat Logo" className='bleat-logo' />
				<div className="form-heading">
					<h1>Hello</h1>
					<p>Forget Your Password</p>
				</div>

				<div className="form-wrapper">
					<form onKeyDown={(event) => checkForEnter(event, passwordReset)}>
						<Grid container>
							<Grid xs={12} item>
								<p className='helper-text'>Your Email</p>
								<TextField onChange={(event) => setEmail(event.target.value)} value={email} variant="outlined" fullWidth required />
							</Grid>
						</Grid>

						{isSubmitting ? <Button className="outlineBtn"><CircularProgress size={20} /></Button> : <Button className="outlineBtn" onClick={passwordReset}>Send Reset Link</Button>}

						<p className="sign-in">Want to Sign In Instead? <button type="button" onClick={goToLoginView}>Click here</button></p>
					</form>
				</div>

				<Snackbar open={openSnackbar} autoHideDuration={2000} onClose={() => setOpenSnackbar(false)} message={snackbarMessage}></Snackbar>
			</div>
		</div>
	);
}

export default Login;
